import { isProblem, type ProblemPayload } from '@assist/contracts';

/**
 * Klien API konsol.
 *
 * SATU jalur data. Konsol tidak punya runtime kedua yang memegang sesi (ADR
 * T1-02), jadi hanya ada satu tempat yang mengirim kredensial dan satu tempat
 * yang menangani kegagalan. Justru di arsitektur dua-jalur, kebocoran
 * multi-tenant lahir — dua tempat harus ingat menyetel konteks tenant, dan satu
 * dari dua akan lupa.
 *
 * Token tidak pernah disentuh JavaScript: cookie `assist_at` bersifat `httpOnly`,
 * jadi `credentials: 'include'` adalah keseluruhan mekanismenya. Sebuah XSS
 * tidak dapat membaca sesi, hanya dapat memakainya selama halaman terbuka.
 */

export class ApiError extends Error {
  readonly problem: ProblemPayload;
  readonly status: number;

  constructor(problem: ProblemPayload, status: number) {
    // `detail_en` untuk log pengembang; TIDAK PERNAH untuk layar (ADR T1-06).
    super(problem.detail_en ?? problem.code);
    this.name = 'ApiError';
    this.problem = problem;
    this.status = status;
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = init;

  const response = await fetch(path, {
    ...rest,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      ...(json !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(headers ?? {}),
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
  });

  if (response.status === 204) return undefined as T;

  let payload: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    if (isProblem(payload)) throw new ApiError(payload, response.status);
    // Jawaban yang bahkan bukan Problem: jangan menampilkan isinya mentah-mentah.
    throw new ApiError(
      { code: 'INTERNAL', status: response.status, detail_en: `HTTP ${response.status}` },
      response.status,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, json?: unknown) => request<T>(path, { method: 'POST', json }),
  patch: <T>(path: string, json?: unknown) => request<T>(path, { method: 'PATCH', json }),
  put: <T>(path: string, json?: unknown) => request<T>(path, { method: 'PUT', json }),
  /**
   * `DELETE` DENGAN BADAN, dan itu sah: RFC 9110 tidak melarangnya, ia hanya mengatakan
   * bahwa maknanya tidak didefinisikan. Di sini maknanya didefinisikan kontrak — alasan
   * pencabutan. Ia tidak dipindahkan ke query string karena alasan yang menyebut nama
   * orang tidak boleh masuk log akses server, dan tidak dijadikan `POST /revoke` karena
   * bentuk rutenya sudah ditetapkan manifest sebagai `DELETE`.
   */
  del: <T>(path: string, json?: unknown) => request<T>(path, { method: 'DELETE', json }),
};

// --- Bentuk jawaban yang dipakai empat layar ---------------------------------

export interface MeResponse {
  id: string;
  full_name: string;
  email: string | null;
  employee_no: string | null;
  job_title: string | null;
  locale: string;
  state: string;
  role_code: string | null;
  actor_kind: string;
  tenant: { id: string; slug: string | null; name: string | null; accent_hex: string | null } | null;
}

export interface NavResponse {
  surface: string;
  groups: Array<{
    group: string;
    order: number;
    items: Array<{
      route: string;
      label_id: string;
      label_en: string;
      icon: string;
      order: number;
      module_code: string;
      /**
       * Lencana hitung `107:218`. OPSIONAL dan dikirim hanya untuk butir yang
       * manifestnya menyatakan `badge`; butir lain tidak menerimanya sama sekali,
       * sehingga tidak ada lencana `0` yang perlu disaring layar.
       */
      badge_count?: number;
    }>;
  }>;
}

/**
 * `GET /v1/notifications` — kotak masuk pengguna, dan pemasok angka lencana bilah atas.
 *
 * `unread_count` dikirim TERPISAH dari `items` karena keduanya menjawab pertanyaan yang
 * berbeda: lencana menyebut berapa yang menunggu, daftar menyebut yang mana. Menghitung
 * lencana dari panjang `items` akan membuat angkanya berhenti benar pada notifikasi
 * ke-21 — dan berhenti benar tanpa ada yang melihatnya.
 */
export interface NotificationsResponse {
  items: Array<{
    id: string;
    event_code: string;
    params: Record<string, unknown>;
    target_route: string;
    severity: string;
    created_at: string;
    read_at: string | null;
  }>;
  unread_count: number;
  page_info: PageInfoShape;
}

export interface EntitlementsResponse {
  tenant_id: string | null;
  modules: Array<{
    module_code: string;
    entitlement_code: string;
    is_core: boolean;
    state: string;
  }>;
}

export interface FindingRef {
  id: string;
  code: string | null;
  full_name?: string;
  name?: string | null;
}

export interface FindingSummary {
  id: string;
  code: string | null;
  title: string;
  description: string | null;
  category_code: string | null;
  location: { id: string; code: string | null; name: string | null } | null;
  reported_by: { id: string; full_name: string | null };
  owner: { id: string; full_name: string | null } | null;
  severity: string | null;
  is_safety: boolean;
  state: string;
  facets: string[];
  escalation_level: number;
  workflow_version: number;
  due_at: string | null;
  opened_at: string;
  closed_at: string | null;
  verified_at: string | null;
  photo_count: number;
}

export interface AvailableTransition {
  code: string;
  label_id: string;
  label_en: string;
  to: string | null;
  reason_required: boolean;
  /**
   * Guard KELAYAKAN yang belum terpenuhi — tombolnya mati dan mengatakan sebabnya.
   * Guard yang memeriksa MASUKAN tidak pernah muncul di sini (UAT D-04).
   */
  blocked_by: string | null;
  /** Medan yang harus dikumpulkan dialog sebelum aksi ini dikirim. */
  requires_input: string[];
}

export interface FindingEvent {
  id: string;
  seq: number;
  event_code: string;
  transition_code: string | null;
  from_state: string | null;
  to_state: string | null;
  actor_name: string | null;
  actor_kind: string;
  reason_code: string | null;
  note: string | null;
  occurred_at: string;
}

export interface FindingDetail extends FindingSummary {
  events: FindingEvent[];
  comments: Array<{
    id: string;
    body: string;
    author_name: string | null;
    created_at: string;
  }>;
  attachments: FindingAttachment[];
  available_transitions: AvailableTransition[];
  /**
   * Dasar pemrosesan AKSI panel temuan, dari manifest rute transisinya.
   *
   * TELAAH KODE 6, M-7. Kaki formulir dirender dari medan ini, bukan dari konstanta di
   * `FindingDetail.tsx` — sumber yang sama dengan yang dicap basis data ke setiap baris
   * riwayat. `null` berarti server tidak menyatakannya, dan layar TIDAK mengarangnya.
   */
  processing_basis: ProcessingBasisView | null;
}

/** Bentuk `processingBasisView()` di server (kernels/platform/src/privacy.ts). */
export interface ProcessingBasisView {
  legal_basis: string;
  /** Pasal UU 27/2022, mis. "20(2)c". Datang dari kosakata, bukan dari layar. */
  article: string;
  purposes: string[];
  subject: string;
  control: string;
  consent_purpose?: string;
}

export interface HomeResponse {
  greeting_name: string;
  locale: string;
  tenant_name: string | null;
  cards: Array<{
    card: string;
    module_code: string;
    counters?: { open: number; overdue: number; awaiting: number; closedThisMonth: number };
    tasks?: Array<{ id: string; code: string; title: string; state: string; due_at: string | null }>;
  }>;
  generated_at: string;
}

export interface LocationRow {
  id: string;
  code: string;
  name: string;
  state: string;
  org_unit_name: string | null;
  /**
   * Butir TERBUKA di area ini, dan persen tuntasnya — keduanya dari SERVER (UAT U2-12).
   *
   * Keduanya `undefined` ketika M02 tidak ter-entitlement, dan layar yang membacanya
   * WAJIB membedakan `undefined` dari `0`: kartu "Kondisi area" pernah menyajikan jumlah
   * area AKTIF sebagai area TUNTAS, dan angka yang salah tetapi selalu hijau adalah
   * angka yang tidak pernah dipertanyakan.
   */
  open_item_count?: number;
  completion_pct?: number;
}

export const endpoints = {
  signIn: (body: { tenant_slug: string | null; identity: string; password: string }) =>
    api.post<{ access_token: string; user: { role_code: string | null; actor_kind: string } }>(
      '/v1/auth/sign-in',
      body,
    ),
  signOut: () => api.post<{ ok: boolean }>('/v1/auth/sign-out'),
  /**
   * UAT D-02. Rute ini ADA dan berjalan sejak awal; yang tidak ada adalah
   * pemanggilnya. Jawabannya SELALU `{ok:true}`, ada atau tidak akunnya — layar
   * karena itu tidak dapat, dan tidak boleh, mengatakan "akunnya ada".
   */
  forgotPassword: (body: { tenant_slug: string | null; identity: string }) =>
    api.post<{ ok: boolean }>('/v1/auth/forgot-password', body),
  me: () => api.get<MeResponse>('/v1/me'),
  nav: () => api.get<NavResponse>('/v1/me/nav'),
  entitlements: () => api.get<EntitlementsResponse>('/v1/me/entitlements'),
  /**
   * Notifikasi — pemasok angka lencana `107:220` di bilah atas.
   *
   * Rutenya terdaftar di manifest M01 sejak tahap 1 dan menjawab `501` sampai 28
   * September 2026: permukaannya ada, pekerjaannya belum. Yang membuatnya dibangun
   * sekarang adalah rancangannya — sebuah lencana yang menyebut angka menuntut angka
   * yang benar, dan tanpa rute tidak ada angka yang benar untuk disebut.
   */
  notifications: (limit = 20) =>
    api.get<NotificationsResponse>(`/v1/notifications?limit=${limit}`),
  home: () => api.get<HomeResponse>('/v1/home'),
  locations: () => api.get<{ items: LocationRow[] }>('/v1/locations'),
  findings: (query: string) =>
    api.get<{
      items: FindingSummary[];
      // Bentuk kontrak (`PageInfo`), bukan `page` buatan sendiri — lihat
      // temuan T-6 telaah kode dan plugin `response-contract`.
      page_info: { has_more: boolean; next_cursor: string | null };
    }>(
      `/v1/findings${query ? `?${query}` : ''}`,
    ),
  finding: (code: string) => api.get<FindingDetail>(`/v1/findings/${encodeURIComponent(code)}`),
  transition: (
    code: string,
    body: { transition: string; reason?: string | null; input?: Record<string, unknown> },
  ) =>
    api.post<{ item: FindingSummary; event: { id: string; event_code: string; to_state: string | null } }>(
      `/v1/findings/${encodeURIComponent(code)}/transitions`,
      body,
    ),
  setLocale: (locale: string) => api.patch<{ locale: string }>('/v1/me', { locale }),
  /**
   * TELAAH 4. Tipe di sini masih `{ users: [...] }` — kunci ALIAS yang DILEPAS
   * bersama V-10, dan `GET /v1/users` sekarang menjawab `{ items, page_info }`.
   * Ia lolos `tsc` karena `api.get<T>()` adalah penegasan tipe, bukan
   * pemeriksaan: siapa pun yang memakainya akan menerima `undefined` saat
   * dijalankan, tanpa satu pun gerbang berbunyi. Diselaraskan dengan bentuk
   * kontrak yang sesungguhnya; `full_name` NULLABLE karena `app_user` ber-RLS
   * dan pemanggil boleh tidak berhak melihat nama seseorang.
   */
  users: () =>
    api.get<{
      items: Array<{ id: string; full_name: string | null; role_code: string | null }>;
      page_info: { has_more: boolean; next_cursor: string | null };
    }>('/v1/users'),

  // -------------------------------------------------------------------------
  // Privasi dan persetujuan (UU 27/2022).
  // -------------------------------------------------------------------------
  /**
   * Pemberitahuan privasi. TANPA token, dan itu bukan kelalaian: layar masuk dan
   * layar terima undangan keduanya terjadi sebelum ada sesi, dan Pasal 21 menuntut
   * pemberitahuan SEBELUM pemrosesan.
   */
  privacyNotice: (tenantSlug?: string | null) =>
    api.get<PrivacyNoticeResponse>(
      `/v1/privacy-notice${tenantSlug ? `?tenant_slug=${encodeURIComponent(tenantSlug)}` : ''}`,
    ),
  /**
   * Siapa yang mengundang — dan ini yang membuat Pasal 21 dapat dipenuhi (UAT U2-04).
   *
   * Nama organisasinya TIDAK dapat datang dari pemberitahuan privasi: satu naskah
   * berlaku bagi semua organisasi, jadi ia tidak dapat menyebut nama salah satunya.
   * Ia datang dari undangannya, tempat ia benar-benar diketahui.
   */
  invitationPreview: (token: string) =>
    api.get<InvitationPreview>(`/v1/auth/invitations/${encodeURIComponent(token)}`),
  acceptInvitation: (body: {
    token: string;
    password: string;
    /**
     * PENGAKUAN telah diberi tahu (Pasal 21) — bukan izin. Namanya diubah bersama
     * kontraknya supaya tidak ada satu pun tempat di klien yang masih menyebut ini
     * "persetujuan": pemrosesannya berdiri di atas hubungan kerja yang ditetapkan
     * organisasinya sebagai PENGENDALI, dan tidak pernah bergantung pada centang ini.
     */
    notice_acknowledged: boolean;
    notice_id?: string;
    locale?: string;
  }) => api.post<{ access_token: string; user: { role_code: string | null } }>(
    '/v1/auth/accept-invitation',
    body,
  ),
  consents: () => api.get<ConsentsResponse>('/v1/me/consents'),
  recordConsent: (body: { purpose: string; granted: boolean; notice_id?: string }) =>
    api.post<{ consent_id: string; purpose: string }>('/v1/me/consents', body),
  withdrawConsent: (purpose: string) =>
    api.post<{ consent_id: string; purpose: string; effect: string }>(
      '/v1/me/consents/withdraw',
      { purpose },
    ),
  /**
   * Jalur unggah berkas — TIGA permintaan, dan berkasnya tidak melewati API (ADR-8).
   *
   * Sebelum perbaikan G3, tidak satu pun dari ketiganya dipanggil konsol: nol
   * `input[type=file]` di seluruh konsol, jadi leader area tidak punya jalan
   * melampirkan foto "sesudah" dan alur 2 §6 gagal dari sisi pengguna (LDR-4).
   */
  uploadIntent: (body: {
    purpose: 'ITEM_PHOTO' | 'ASSESSMENT_EVIDENCE' | 'AVATAR' | 'TENANT_LOGO';
    mime_type: 'image/jpeg' | 'image/png' | 'image/webp';
    byte_size?: number;
    filename?: string;
  }) => api.post<UploadIntentResult>('/v1/files/upload-intents', body),
  commitFile: (fileId: string, body: { byte_size: number; checksum_sha256: string }) =>
    api.post<{ state: string; verification_required: boolean }>(
      `/v1/files/${encodeURIComponent(fileId)}/commit`,
      body,
    ),
  downloadUrl: (fileId: string) =>
    api.get<{ download_url: string; expires_at: string; mime_type: string }>(
      `/v1/files/${encodeURIComponent(fileId)}/download-url`,
    ),
  attachFile: (
    code: string,
    body: { file_object_id: string; role: 'BEFORE' | 'AFTER' | 'EVIDENCE'; caption?: string },
  ) =>
    api.post<{ id: string; role: string; attachment_wait_ms: number }>(
      `/v1/findings/${encodeURIComponent(code)}/attachments`,
      body,
    ),
  /** Undangan — layar persiapan organisasi (UAT U2-02). */
  invite: (body: {
    email: string;
    full_name: string;
    role_code: 'OPR' | 'LDR' | 'MGR' | 'AUD' | 'FAC';
    employee_no?: string | null;
  }) => api.post<InviteResult>('/v1/invitations', body),
  tenantReadiness: () => api.get<TenantReadiness>('/v1/tenant/readiness'),
  processingRegister: () => api.get<ProcessingRegisterResponse>('/v1/processing-register'),
  tenantAgreement: () => api.get<TenantAgreementResponse>('/v1/tenant/processing-agreement'),
  tenantAudit: (limit = 50) => api.get<TenantAuditResponse>(`/v1/tenant/audit?limit=${limit}`),

  /**
   * IDENTITAS ORGANISASI — dua lingkup, dua entri, dan itu bukan kebetulan.
   *
   * `tenantIdentity*` tidak menerima tenant id sama sekali; `platformTenantUpdate`
   * menerimanya di jalur. Bentuk itu adalah ADR-I1 yang terlihat dari sisi klien:
   * tidak ada satu pun tempat di konsol tempat seorang manajer dapat mengetikkan
   * organisasi lain, karena tidak ada parameter untuk mengetikkannya.
   *
   * `tenantIdentity()` dipanggil oleh LAYAR yang merender logonya, pada saat ia
   * merendernya — `logo_mark_url` berumur 90 detik, dan menaruhnya di `GET /v1/me`
   * (yang dipanggil sekali per sesi) berarti gambar rusak bagi siapa pun yang membuka
   * Dasbor lebih dari satu setengah menit setelah masuk.
   */
  tenantIdentity: () => api.get<TenantIdentity>('/v1/tenant/identity'),
  tenantIdentityUpdate: (body: TenantIdentityPatch) =>
    api.patch<TenantIdentity>('/v1/tenant/identity', body),
  platformTenantUpdate: (tenantId: string, body: PlatformTenantPatch) =>
    api.patch<Record<string, unknown>>(
      `/v1/platform/tenants/${encodeURIComponent(tenantId)}`,
      body,
    ),

  // ===========================================================================
  // GELOMBANG LAYAR KONSOL
  //
  // Satu entri per operasi, dan TIPENYA DITURUNKAN dari `api/openapi.yaml` —
  // bukan ditebak dari bentuk jawaban yang kebetulan terlihat saat menulis layar.
  // `api.get<T>()` adalah PENEGASAN tipe, bukan pemeriksaan (telaah 4, V-10: tipe
  // `{ users: [...] }` lolos `tsc` selama berbulan-bulan sementara servernya sudah
  // menjawab `{ items, page_info }`, dan pemakainya menerima `undefined` saat
  // dijalankan tanpa satu pun gerbang berbunyi). Jadi setiap bentuk di bawah
  // ditulis dengan membaca kontraknya.
  // ===========================================================================

  /** Lapor temuan dari konsol — alur 1 §6, jalur yang sebelumnya hanya ada di ponsel. */
  createFinding: (body: {
    id?: string;
    title: string;
    description?: string | null;
    category_code: string;
    location_id: string;
    asset_id?: string | null;
    severity?: string | null;
    is_safety?: boolean;
    attachments?: Array<{ file_object_id: string; role: string; caption?: string }>;
  }) => api.post<FindingSummary & { attachment_wait_ms: number }>('/v1/findings', body),
  verificationQueue: () =>
    api.get<{ items: FindingSummary[]; page_info: PageInfoShape }>('/v1/verification-queue'),
  itemCategories: () =>
    api.get<{
      items: Array<{ code: string; name: string; type_code: string }>;
      page_info: PageInfoShape;
      /** Dasar pemrosesan `POST /v1/findings` — dibaca layar Lapor Temuan. */
      processing_basis: ProcessingBasisView | null;
    }>('/v1/item-categories'),

  /** Dasbor — bentuk `Dashboard` kontrak (kartu, kondisi area, dampak terverifikasi). */
  /** `periode` berbentuk `YYYY-MM` — pemilih bilah atas `5:58`. */
  dashboard: (periode?: string) =>
    api.get<DashboardResponse>(`/v1/dashboard${periode ? `?period=${periode}` : ''}`),
  decisionQueue: () =>
    api.get<{ items: Array<FindingSummary & { queue_reason: string }>; page_info: PageInfoShape }>(
      '/v1/dashboard/decision-queue',
    ),
  metrics: (from?: string, to?: string) =>
    api.get<MetricsResponse>(
      `/v1/metrics${from && to ? `?from=${from}&to=${to}` : ''}`,
    ),
  reportCatalog: () => api.get<ReportCatalogResponse>('/v1/report-catalog'),
  savedReports: () =>
    api.get<{ items: SavedReportRow[]; page_info: PageInfoShape; processing_basis: ProcessingBasisView | null }>(
      '/v1/saved-reports',
    ),
  createSavedReport: (body: {
    code: string;
    name: string;
    report_kind: string;
    format: string;
    schedule_cron?: string | null;
    recipients?: Array<{ email: string }>;
  }) => api.post<SavedReportRow>('/v1/saved-reports', body),
  deleteSavedReport: (id: string) =>
    api.del<{ ok: boolean }>(`/v1/saved-reports/${encodeURIComponent(id)}`),
  runSavedReport: (id: string) =>
    api.post<ReportRunRow>(`/v1/saved-reports/${encodeURIComponent(id)}/runs`),
  search: (q: string, type?: string) =>
    api.get<SearchResponse>(
      `/v1/search?q=${encodeURIComponent(q)}${type ? `&type=${encodeURIComponent(type)}` : ''}`,
    ),

  /** Rapat harian dan eskalasi — M07. */
  meetings: (state?: string) =>
    api.get<{ items: MeetingRow[]; page_info: PageInfoShape }>(
      `/v1/meetings${state ? `?state=${encodeURIComponent(state)}` : ''}`,
    ),
  meeting: (id: string) => api.get<MeetingDetail>(`/v1/meetings/${encodeURIComponent(id)}`),
  createMeeting: (body: {
    org_unit_id: string;
    location_id?: string | null;
    scheduled_at: string;
  }) => api.post<MeetingRow>('/v1/meetings', body),
  addMeetingItem: (meetingId: string, body: { title: string; item_id?: string | null }) =>
    api.post<MeetingItemRow>(`/v1/meetings/${encodeURIComponent(meetingId)}/items`, body),
  patchMeetingItem: (
    meetingId: string,
    itemId: string,
    body: { discussed?: boolean; outcome_code?: string | null; note?: string | null },
  ) =>
    api.patch<MeetingItemRow>(
      `/v1/meetings/${encodeURIComponent(meetingId)}/items/${encodeURIComponent(itemId)}`,
      body,
    ),
  meetingTransition: (meetingId: string, body: { transition: string; reason?: string | null }) =>
    api.post<MeetingDetail>(`/v1/meetings/${encodeURIComponent(meetingId)}/transitions`, body),
  meetingAttendance: (
    meetingId: string,
    body: { attendees: Array<{ user_id: string; present: boolean }> },
  ) =>
    api.put<{ items: Array<{ user: FindingRef; present: boolean }> }>(
      `/v1/meetings/${encodeURIComponent(meetingId)}/attendance`,
      body,
    ),
  escalations: () => api.get<EscalationsResponse>('/v1/escalations'),
  escalationRules: () => api.get<EscalationRulesResponse>('/v1/escalation-rules'),
  putEscalationRules: (body: {
    levels: Array<{ level: number; target_role_code: string | null; sla_hours: number }>;
  }) => api.put<EscalationRulesResponse>('/v1/escalation-rules', body),
  /** `periodeBulan` berbentuk `YYYY-MM-01`, seperti kolom `spotlight_entry.period_month`. */
  spotlight: (periodeBulan?: string) =>
    api.get<SpotlightResponse>(
      `/v1/spotlight${periodeBulan ? `?period_month=${periodeBulan}` : ''}`,
    ),
  addSpotlight: (body: {
    subject_kind: string;
    subject_id: string;
    headline_id: string;
    headline_en: string;
    note?: string | null;
  }) => api.post<SpotlightRow>('/v1/spotlight', body),
  removeSpotlight: (id: string) =>
    api.del<{ ok: boolean }>(`/v1/spotlight/${encodeURIComponent(id)}`),

  /** Poin — M10. */
  pointRules: () => api.get<PointRulesResponse>('/v1/point-rules'),
  createPointRule: (body: {
    code: string;
    trigger_event: string;
    subject_role: string;
    points: number;
    award_state: string;
    cap_period: string;
    cap_value?: number | null;
  }) => api.post<PointRuleRow>('/v1/point-rules', body),
  patchPointRule: (code: string, body: { state?: string; points?: number }) =>
    api.patch<PointRuleRow>(`/v1/point-rules/${encodeURIComponent(code)}`, body),
  pointLedger: (approvalState?: string) =>
    api.get<PointLedgerResponse>(
      `/v1/point-ledger${approvalState ? `?approval_state=${encodeURIComponent(approvalState)}` : ''}`,
    ),
  approvePoint: (id: string) =>
    api.post<PointLedgerRow>(`/v1/point-ledger/${encodeURIComponent(id)}/approve`),
  reversePoint: (id: string, reason: string) =>
    api.post<PointLedgerRow>(`/v1/point-ledger/${encodeURIComponent(id)}/reverse`, { reason }),
  /** `periodeBulan` berbentuk `YYYY-MM-01`. */
  leaderboard: (periodeBulan?: string) =>
    api.get<LeaderboardResponse>(
      `/v1/leaderboard${periodeBulan ? `?period_month=${periodeBulan}` : ''}`,
    ),

  /** Kelola — M01. */
  orgUnits: () =>
    api.get<{ items: Array<{ id: string; code: string; name: string; parent_id: string | null; depth: number }>; page_info: PageInfoShape }>(
      '/v1/org-units',
    ),
  createLocation: (body: {
    code: string;
    name: string;
    org_unit_id: string;
    description?: string | null;
  }) => api.post<LocationRow>('/v1/locations', body),
  deactivateLocation: (id: string) =>
    api.post<LocationRow>(`/v1/locations/${encodeURIComponent(id)}/deactivate`),
  userDetail: (id: string) => api.get<UserDetailRow>(`/v1/users/${encodeURIComponent(id)}`),
  invitations: () =>
    api.get<{ items: InvitationRow[]; page_info: PageInfoShape }>('/v1/invitations'),
  revokeInvitation: (id: string) =>
    api.del<{ ok: boolean }>(`/v1/invitations/${encodeURIComponent(id)}`),
  mySessions: () =>
    api.get<{ items: Array<{ id: string; device_label: string | null; last_seen_at: string | null; created_at: string; current: boolean }>; page_info: PageInfoShape }>(
      '/v1/me/sessions',
    ),
  revokeSession: (id: string) =>
    api.del<{ ok: boolean }>(`/v1/me/sessions/${encodeURIComponent(id)}`),
  patchMe: (body: { job_title?: string | null; locale?: string }) =>
    api.patch<MeResponse>('/v1/me', body),

  /** Platform dan portofolio konsultan — M15. */
  platformTenants: () =>
    api.get<{ items: TenantRow[]; page_info: PageInfoShape }>('/v1/platform/tenants'),
  platformTenant: (id: string) =>
    api.get<TenantRow>(`/v1/platform/tenants/${encodeURIComponent(id)}`),
  /**
   * EMPAT LANGKAH "ORGANISASI BARU SAMPAI DAPAT DIPAKAI", seluruhnya lewat HTTP.
   *
   * Sebelum gelombang ini ketiga rute di bawah ADA dan bekerja, dan tidak satu pun
   * dipanggil konsol: pemilik proyek membuat organisasi pertamanya dengan `curl`.
   * Langkah 3 tidak punya baris sendiri karena ia BERJALAN SENDIRI di dalam langkah 2
   * — `createTenantAgreement` mengembalikan hasilnya di `provisioning` — dan
   * `rerunProvisioning` ada untuk satu-satunya keadaan di mana ia tidak berjalan:
   * ketika penyediaan otomatis itu gagal.
   */
  createTenant: (body: {
    slug: string;
    name: string;
    legal_name?: string | null;
    plan_code: string;
    default_locale: 'id-ID' | 'en-US';
    timezone: string;
  }) => api.post<TenantRow>('/v1/platform/tenants', body),
  acceptProcessingAgreement: (
    tenantId: string,
    body: { accepted_by_name: string; accepted_by_title: string; locale?: 'id-ID' | 'en-US' },
  ) =>
    api.post<{
      acceptance_id: string;
      agreement_version: number;
      tenant_state: string;
      provisioning: { ok: boolean; item_categories?: number; workflows?: string[]; error?: string };
    }>(`/v1/platform/tenants/${encodeURIComponent(tenantId)}/processing-agreement`, body),
  rerunProvisioning: (tenantId: string) =>
    api.post<ProvisioningResult>(
      `/v1/platform/tenants/${encodeURIComponent(tenantId)}/provisioning`,
      {},
    ),
  inviteFirstManager: (
    tenantId: string,
    body: {
      email: string;
      full_name: string;
      job_title?: string | null;
      employee_no?: string | null;
      locale?: 'id-ID' | 'en-US' | null;
    },
  ) =>
    api.post<FirstManagerInvitationResult>(
      `/v1/platform/tenants/${encodeURIComponent(tenantId)}/invitations`,
      body,
    ),
  /**
   * Bentuknya `{tenant_id, plan_code, etag, modules[]}` — BUKAN `{items, page_info}`.
   *
   * Ini kelas cacat V-10 telaah 4: `api.get<T>()` adalah PENEGASAN tipe, bukan pemeriksaan,
   * jadi bentuk yang salah lolos `tsc` dan pemakainya menerima `undefined` saat dijalankan
   * tanpa satu pun gerbang berbunyi. Yang ditulis di sini adalah yang benar-benar dikirim
   * `GET /v1/platform/tenants/{id}/entitlements`, diperiksa dengan memanggilnya.
   */
  platformTenantEntitlements: (id: string) =>
    api.get<{
      tenant_id: string;
      plan_code: string | null;
      etag: string;
      modules: Array<{ code: string; state: string; module_code?: string; is_core?: boolean }>;
    }>(`/v1/platform/tenants/${encodeURIComponent(id)}/entitlements`),
  setEntitlement: (tenantId: string, moduleCode: string, state: string) =>
    api.put<{ module_code: string; state: string }>(
      `/v1/platform/tenants/${encodeURIComponent(tenantId)}/entitlements/${encodeURIComponent(moduleCode)}`,
      { state },
    ),
  /**
   * W-1 / C-16 — peran platform. `POST` dan `DELETE` keduanya menuntut ALASAN, dan
   * alasannya ada di BADAN permintaan bahkan untuk `DELETE`: alasan adalah bagian dari
   * perbuatannya, dan meletakkannya di query string akan menaruhnya di log akses server
   * bersama setiap permintaan lain — termasuk alasan yang menyebut nama orang.
   */
  platformMemberships: () =>
    api.get<{ items: PlatformMembershipRow[]; page_info: PageInfoShape }>(
      '/v1/platform/memberships',
    ),
  platformMembershipSet: (body: {
    email: string;
    full_name: string;
    role_code: 'PLT' | 'KON';
    job_title?: string | null;
    reason: string;
  }) => api.post<PlatformMembershipRow>('/v1/platform/memberships', body),
  platformMembershipRevoke: (userId: string, body: { reason: string }) =>
    api.del<PlatformMembershipRow>(
      `/v1/platform/memberships/${encodeURIComponent(userId)}`,
      body,
    ),
  platformModules: () =>
    api.get<{ items: ModuleRegistryRow[]; page_info: PageInfoShape }>('/v1/platform/modules'),
  platformPlans: () =>
    api.get<{ items: Array<{ code: string; name: string; modules: string[] }>; page_info: PageInfoShape }>(
      '/v1/platform/plans',
    ),
  platformAudit: (limit = 50) =>
    api.get<TenantAuditResponse>(`/v1/platform/audit?limit=${limit}`),
  platformHealth: () => api.get<SystemHealthResponse>('/v1/platform/health'),

  /**
   * GELOMBANG M15 — LIMA BELAS RUTE YANG SEBELUMNYA MENJAWAB `501`.
   *
   * Setiap baris di bawah punya kendalinya di layar pada gelombang yang SAMA. Itu
   * bukan kebetulan melainkan aturan yang berlaku dua arah: sebuah rute tanpa
   * kendali membuat layar berkata "belum dapat dilakukan dari sini", dan sebuah
   * kendali tanpa rute adalah tombol yang tidak melakukan apa pun. Keduanya cacat,
   * dan keduanya punya gerbangnya.
   */
  suspendTenant: (tenantId: string, body: { reason?: string }) =>
    api.post<TenantRow>(`/v1/platform/tenants/${encodeURIComponent(tenantId)}/suspend`, body),
  resumeTenant: (tenantId: string, body: { reason?: string }) =>
    api.post<TenantRow>(`/v1/platform/tenants/${encodeURIComponent(tenantId)}/resume`, body),
  setTenantPlan: (tenantId: string, body: { plan_code: string; reason?: string }) =>
    api.put<TenantRow>(`/v1/platform/tenants/${encodeURIComponent(tenantId)}/plan`, body),
  tenantUsage: (tenantId: string) =>
    api.get<UsageReportResponse>(`/v1/platform/tenants/${encodeURIComponent(tenantId)}/usage`),
  tenantProvisioningLog: (tenantId: string) =>
    api.get<TenantAuditResponse>(
      `/v1/platform/tenants/${encodeURIComponent(tenantId)}/provisioning-log`,
    ),

  platformIncidents: (state?: 'OPEN' | 'CLOSED') =>
    api.get<{ items: IncidentRow[]; page_info: PageInfoShape }>(
      `/v1/platform/incidents${state ? `?state=${state}` : ''}`,
    ),
  openIncident: (body: {
    severity: 'SEV1' | 'SEV2' | 'SEV3';
    title: string;
    detail?: string;
    related_job_code?: string;
  }) => api.post<IncidentRow>('/v1/platform/incidents', body),
  patchIncident: (
    incidentId: string,
    body: {
      action: 'CLOSE' | 'REOPEN' | 'AMEND';
      severity?: 'SEV1' | 'SEV2' | 'SEV3';
      detail?: string;
      reason?: string;
    },
  ) => api.patch<IncidentRow>(`/v1/platform/incidents/${encodeURIComponent(incidentId)}`, body),

  platformJobs: (state?: string) =>
    api.get<{ items: JobRunRow[]; page_info: PageInfoShape }>(
      `/v1/platform/jobs${state ? `?state=${state}` : ''}`,
    ),
  retryJob: (jobRunId: string) =>
    api.post<JobRunRow>(`/v1/platform/jobs/${encodeURIComponent(jobRunId)}/retry`, {}),

  consultantAssignments: (tenantId?: string) =>
    api.get<{ items: ConsultantAssignmentRow[]; page_info: PageInfoShape }>(
      `/v1/platform/consultant-assignments${tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ''}`,
    ),
  assignConsultant: (body: { consultant_user_id: string; tenant_id: string }) =>
    api.post<ConsultantAssignmentRow>('/v1/platform/consultant-assignments', body),
  revokeConsultant: (consultantUserId: string, tenantId: string) =>
    api.del<{ ok: boolean; audit_log_id: string | null }>(
      `/v1/platform/consultant-assignments/${encodeURIComponent(consultantUserId)}/${encodeURIComponent(tenantId)}`,
    ),

  auditAnchors: (chainKey?: string) =>
    api.get<{ items: AuditAnchorRow[]; page_info: PageInfoShape }>(
      `/v1/platform/audit/anchors${chainKey ? `?chain_key=${encodeURIComponent(chainKey)}` : ''}`,
    ),
  auditIntegrity: (chainKey = 'PLATFORM') =>
    api.get<{ chain_key: string; checked: number; ok: boolean; first_broken_seq: number | null }>(
      `/v1/platform/audit/integrity?chain_key=${encodeURIComponent(chainKey)}`,
    ),
  exportAudit: (body: { from?: string; to?: string; tenant_id?: string; event_code?: string }) =>
    api.post<AuditExportResponse>('/v1/platform/audit/exports', { format: 'CSV', ...body }),
  myAssignments: () =>
    api.get<{ items: ClientPortfolioRow[]; page_info: PageInfoShape }>('/v1/me/assignments'),
  switchTenant: (slug: string) =>
    api.post<{ tenant_id: string | null; tenant_slug: string }>('/v1/auth/context/tenant', {
      tenant_slug: slug,
    }),
};

export interface PageInfoShape {
  has_more: boolean;
  next_cursor: string | null;
  total_estimate?: number | null;
}

export interface DashboardResponse {
  period: { from: string; to: string };
  cards: Array<{
    code: string;
    module_code: string;
    value: number | null;
    unit: string | null;
    delta_pct: number | null;
    series: Array<Record<string, unknown>>;
  }>;
  areas: Array<{
    location: { id: string; code: string; name: string };
    open_count: number;
    overdue_count: number;
    completion_pct: number | null;
    last_audit_pct: number | null;
  }>;
  verified_impact: {
    amount: number;
    currency: string;
    /** Klaim yang BELUM disahkan keuangan dan karena itu tidak masuk angka di atas. */
    excluded_unverified_count: number;
  };
}

export interface MetricsResponse {
  period: { from: string; to: string };
  daily: Array<{
    day: string;
    type_code: string;
    category_code: string | null;
    opened: number;
    closed: number;
    verified: number;
    overdue: number;
    escalated: number;
  }>;
  by_category: Array<{ category_code: string | null; opened: number; closed: number }>;
  verified_impact: {
    amount: number;
    currency: string;
    claim_count: number;
    excluded_unverified_count: number;
    excluded_unverified_amount: number;
  };
  /** Salah bila pekerja agregasi belum pernah berjalan — BEDA dari "periode tanpa data". */
  aggregates_ready: boolean;
}

export interface ReportCatalogResponse {
  items: Array<{ code: string; module_code: string; group: string }>;
  formats: string[];
  /** Salah selama pekerja penghasil berkas belum digelar. Layar mengatakannya. */
  export_worker_configured: boolean;
}

export interface SavedReportRow {
  id: string;
  code: string;
  name: string;
  report_kind: string;
  schedule_cron: string | null;
  format: string;
  recipients: Array<{ email?: string }>;
  state: string;
  last_run_at: string | null;
}

export interface ReportRunRow {
  id: string;
  report_kind: string;
  format: string;
  state: string;
  download_url: string | null;
  row_count: number | null;
  requested_at: string;
}

export interface SearchResponse {
  query: string;
  facets: Array<{ entity_type: string; count: number }>;
  results: Array<{
    entity_type: string;
    entity_id: string;
    ref_code: string | null;
    title: string;
    snippet: string;
    route: string;
    module_code: string;
  }>;
  page_info: PageInfoShape;
}

export interface MeetingRow {
  id: string;
  kind: string;
  org_unit_id: string;
  facilitator: { id: string; full_name: string | null };
  state: string;
  timebox_seconds: number;
  elapsed_seconds: number | null;
  scheduled_at: string;
  started_at: string | null;
  closed_at: string | null;
  minutes_url: string | null;
  item_count?: number;
  discussed_count?: number;
  present_count?: number;
}

export interface MeetingItemRow {
  id: string;
  seq: number;
  title: string;
  item_code: string | null;
  discussed_at: string | null;
  outcome_code: string | null;
  action_item_code: string | null;
  note?: string | null;
}

export interface MeetingDetail extends MeetingRow {
  items: MeetingItemRow[];
  attendance: Array<{ user: { id: string; full_name: string | null }; present: boolean }>;
  available_transitions: AvailableTransition[];
  facets: string[];
  agenda_candidates: FindingSummary[];
  processing_basis: ProcessingBasisView | null;
}

export interface EscalationsResponse {
  items: Array<{
    id: string;
    item: FindingSummary;
    level: number;
    trigger_code: string;
    target_role_code: string;
    assignee: { id: string; full_name: string | null } | null;
    opened_at: string;
    due_at: string;
    closed_at: string | null;
    overdue_at_level: boolean;
  }>;
  /** BENAR berarti kondisi BAIK — bukan kosong netral (peta §5.3). */
  nothing_escalated: boolean;
  by_level: Array<{ level: number; open_count: number }>;
}

export interface EscalationRulesResponse {
  levels: Array<{
    level: number;
    target_role_code: string;
    target_kind?: string;
    sla_hours: number;
    triggers: string[];
    state: string;
  }>;
  processing_basis?: ProcessingBasisView | null;
}

export interface SpotlightRow {
  id: string;
  period_month: string;
  subject_kind: string;
  subject_id: string;
  headline: string;
  headline_id: string;
  headline_en: string;
  note: string | null;
  subject_label?: string | null;
}

export interface SpotlightResponse {
  items: SpotlightRow[];
  processing_basis: ProcessingBasisView | null;
}

export interface PointRuleRow {
  code: string;
  trigger_event: string;
  subject_role: string;
  points: number;
  award_state: string;
  cap_period: string;
  cap_value: number | null;
  requires_approval: boolean;
  state: string;
}

export interface PointRulesResponse {
  items: PointRuleRow[];
  trigger_events: string[];
  processing_basis: ProcessingBasisView | null;
}

export interface PointLedgerRow {
  id: string;
  user: { id: string; full_name: string | null };
  rule_code: string;
  points: number;
  state: string;
  period_month: string;
  approval_state: string;
  awarded_at: string;
  reversed_at?: string | null;
  reversal_reason?: string | null;
}

export interface PointLedgerResponse {
  items: PointLedgerRow[];
  page_info: PageInfoShape;
  page_totals: { rows: number; final_points: number; pending_approval: number };
}

export interface LeaderboardResponse {
  scope_kind: string;
  period_month: string;
  me: { rank: number; final_points: number; provisional_points: number; target_points: number };
  rows: Array<{
    rank: number;
    user: { id: string; full_name: string | null };
    final_points: number;
    provisional_points?: number;
  }>;
}

export interface UserDetailRow {
  id: string;
  full_name: string | null;
  email: string | null;
  employee_no: string | null;
  job_title: string | null;
  state: string;
  role_code: string | null;
  locale?: string;
  last_login_at?: string | null;
}

export interface InvitationRow {
  id: string;
  email: string;
  full_name: string;
  role_code: string;
  state: string;
  expires_at: string;
  created_at?: string;
}

export interface TenantRow {
  id: string;
  slug: string;
  name: string;
  legal_name?: string | null;
  plan_code: string | null;
  state: string;
  default_locale?: string;
  timezone?: string;
  activated_at?: string | null;
  created_at?: string;
  user_count?: number | null;
  /** Hanya pada GET detail. Lihat `TenantOnboarding`. */
  onboarding?: TenantOnboarding;
}

/**
 * APA YANG MASIH KURANG sebelum sebuah organisasi dapat dipakai.
 *
 * `remaining_steps` adalah KODE, bukan kalimat (ADR T1-06): layar merendernya dalam
 * bahasa profil pembacanya. Sebuah daftar langkah berbahasa Inggris pada layar
 * persiapan organisasi Indonesia adalah D-05 dalam bentuk baru.
 */
export interface TenantOnboarding {
  /**
   * `false` berarti angkanya TIDAK TERBACA — bukan nol. Layar wajib membedakan
   * keduanya: "nol kategori" pada organisasi yang sebenarnya lengkap menyuruh admin
   * platform menjalankan ulang penyediaan yang tidak perlu dijalankan.
   */
  readable: boolean;
  agreement_accepted?: boolean;
  item_categories?: number;
  workflows?: number;
  active_managers?: number;
  open_invitations?: number;
  remaining_steps?: string[];
  operable?: boolean;
}

/** Jawaban `POST /v1/platform/tenants/{tenantId}/invitations`. */
export interface FirstManagerInvitationResult {
  id: string;
  invitation_id: string;
  email: string;
  full_name?: string;
  role_code: string;
  state: string;
  first_manager?: boolean;
  reinvited?: boolean;
  tenant?: { id: string; slug: string; name: string };
  delivery_state: string;
  delivery_configured: boolean;
  transport?: string;
  /**
   * Tautan penerimaan — ADA hanya ketika ASSIST tidak dapat mengantarkannya sendiri.
   * SEKALI LIHAT: ia tidak dapat dibaca ulang dari mana pun, jadi layar yang
   * menampilkannya tidak boleh membuangnya tanpa mengatakan begitu.
   */
  invitation_url: string | null;
}

/** Jawaban `POST /v1/platform/tenants/{tenantId}/provisioning`. */
export interface ProvisioningResult {
  tenant_id: string;
  item_categories: number;
  workflows: string[];
}

export interface ModuleRegistryRow {
  code: string;
  name: string;
  kernel_owner?: string | null;
  entitlement_code?: string | null;
  is_core: boolean;
  version?: string;
  requires?: string[];
}

export interface SystemHealthResponse {
  outbox_pending?: number;
  outbox_failed?: number;
  job_failures?: number;
  open_incidents?: number;
  checked_at: string;
  [key: string]: unknown;
}

/**
 * Bentuk baris gelombang M15. Ia ditulis dari `api/openapi.yaml` dan DIPERIKSA dengan
 * memanggil rutenya — bukan diketik dari ingatan: `api.get<T>()` adalah penegasan tipe,
 * bukan pemeriksaan, dan bentuk yang salah lolos `tsc` lalu menghasilkan `undefined`
 * saat dijalankan tanpa satu pun gerbang berbunyi (kelas V-10 telaah 4).
 */
export interface IncidentRow {
  id: string;
  severity: 'SEV1' | 'SEV2' | 'SEV3';
  title: string;
  detail: string | null;
  related_job_code: string | null;
  /** DITURUNKAN dari `closed_at` di server, bukan kolom basis data (ADR-M15-3). */
  state: 'OPEN' | 'CLOSED';
  opened_at: string;
  closed_at: string | null;
}

export interface JobRunRow {
  id: string;
  tenant_id: string | null;
  module_code: string;
  job_code: string;
  state: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'SKIPPED_NO_ENTITLEMENT';
  attempts: number;
  scheduled_at: string;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  error_code: string | null;
  error_detail: string | null;
  retry_of: string | null;
  requested_by: string | null;
}

export interface ConsultantAssignmentRow {
  consultant_user_id: string;
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  consultant_name: string | null;
  consultant_email: string | null;
  granted_by: string;
  granted_by_name: string | null;
  granted_at: string;
  revoked_at: string | null;
}

export interface AuditAnchorRow {
  chain_key: string;
  chain_seq: number;
  row_hash: string;
  anchored_at: string;
  anchor_ref: string;
}

export interface UsageReportResponse {
  tenant_id: string;
  period_month: string;
  meters: Array<{
    module_code: string;
    metered_by: string;
    /** NULL berarti belum ada yang mengukurnya — BUKAN nol (kelas UAT U2-12b). */
    value: number | null;
    quota_value: number | null;
  }>;
}

export interface AuditExportResponse {
  id: string;
  format: 'CSV';
  state: 'READY';
  row_count: number;
  filename: string;
  media_type: string;
  content_base64: string;
  requested_at: string;
}

export interface ClientPortfolioRow {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  state: string;
  plan_code: string | null;
  granted_at: string;
}

export interface PrivacyNoticeEntry {
  id: string;
  locale: string;
  version: number;
  title: string;
  body: string;
  /**
   * NAMA Pengendali, atau `null` bila naskahnya bawaan PQM (UAT U2-04).
   *
   * Kolom ini pernah berisi KALIMAT, dan layar terima undangan menyisipkannya tiga kali
   * ke tempat nama — menghasilkan prosa yang tidak dapat diikuti pada satu-satunya layar
   * yang memenuhi Pasal 21. Ia sekarang `null` untuk naskah bawaan, dan nama
   * organisasinya datang dari `endpoints.invitationPreview()`.
   */
  controller_name: string | null;
  /** Kalimat yang menerangkan siapa Pengendalinya. Dipakai sebagai KALIMAT. */
  controller_statement: string;
  contact: string;
  effective_from: string;
  tenant_published: boolean;
}

export interface UploadIntentResult {
  file_id: string;
  upload_url: string;
  expires_at: string;
  max_byte_size: number;
  max_pixel_edge: number | null;
  verification_required: boolean;
}

/** Jawaban `POST /v1/invitations`; `delivery_*` dinyatakan APA ADANYA. */
export interface InviteResult {
  id: string;
  email: string;
  full_name: string;
  role_code: string;
  state: string;
  reinvited: boolean;
  delivery_state: 'QUEUED' | 'WRITTEN_TO_FILE' | 'NOT_CONFIGURED';
  delivery_configured: boolean;
  transport: string;
}

export interface TenantReadiness {
  tenant_id: string;
  counts: Record<string, number>;
  remaining_steps: string[];
  operable: boolean;
  delivery_state: 'QUEUED' | 'WRITTEN_TO_FILE' | 'NOT_CONFIGURED';
  delivery_configured: boolean;
}

/** Satu lampiran temuan. Dipakai `PhotoAttachments` untuk menampilkan DAN mengunggah. */
export interface FindingAttachment {
  id: string;
  role: string;
  file_object_id: string;
  caption: string | null;
}

/** Jawaban `GET /v1/auth/invitations/{token}`. */
export interface InvitationPreview {
  organisation_name: string;
  organisation_slug: string;
  email_hint: string;
  role_code: string;
  expires_at: string;
  default_locale: string;
}

export interface PrivacyNoticeResponse {
  locale: string;
  notices: PrivacyNoticeEntry[];
}

/** Satu baris layar M15 · Platform — Anggota (W-1 / C-16). */
export interface PlatformMembershipRow {
  user_id: string;
  email: string;
  full_name: string | null;
  job_title: string | null;
  role_code: 'PLT' | 'KON';
  state: 'ACTIVE' | 'SUSPENDED';
  user_state: string;
  has_password: boolean;
  granted_at: string;
  granted_by_name: string | null;
  revoked_at: string | null;
  /**
   * DIHITUNG SERVER, bukan di layar. Sebuah layar yang menghitungnya sendiri dari daftar
   * yang ia pegang akan salah pada saat dua orang membuka daftar yang sama, dan tombol
   * `Cabut` yang hidup pada admin terakhir adalah tombol yang menjanjikan sesuatu yang
   * trigger basis data akan menolak.
   */
  is_last_active_admin?: boolean;
}

export interface ConsentHistoryRow {
  id: string;
  purpose: string;
  kind: 'CONSENT' | 'ACKNOWLEDGEMENT';
  basis: string;
  purposes: string[];
  notice_version: number | null;
  notice_locale: string | null;
  granted_at: string | null;
  withdrawn_at: string | null;
  recorded_at: string;
}

export interface ConsentsResponse {
  items: ConsentHistoryRow[];
  page_info: { has_more: boolean; next_cursor: string | null };
  state: Array<{
    purpose: string;
    kind: 'CONSENT' | 'ACKNOWLEDGEMENT';
    granted: boolean;
    withdrawal_effect: string;
  }>;
}

/**
 * Daftar pemrosesan — dibaca TANPA sesi, dan itu yang membuatnya berguna: organisasi
 * yang sedang memutuskan apakah akan menandatangani perjanjian pemrosesan data belum
 * punya akun. Yang datang dari server adalah KODE; kalimatnya dirender klien dari
 * katalog dua bahasa.
 */
export interface ProcessingRegisterResponse {
  write_operation_count: number;
  modules: Array<{
    code: string;
    name: string;
    write_operations: number;
    categories: string[];
    purposes: string[];
  }>;
  categories: Array<{
    code: string;
    purposes: string[];
    legal_bases: string[];
    modules: string[];
  }>;
  purposes: Array<{ code: string; categories: string[]; modules: string[] }>;
  legal_bases: Array<{ code: string; article: string; write_operations: number }>;
  declared_but_unused: string[];
  processing_locations: Array<{
    aspect: string;
    region: string;
    country: string;
    declared_at: string;
  }>;
  subprocessors: Array<{
    name: string;
    role: string;
    country: string;
    added_at: string;
    notified_at: string | null;
  }>;
  locations_declared: boolean;
  subprocessors_declared: boolean;
}

export interface TenantAgreementResponse {
  agreement: {
    id: string;
    locale: string;
    version: number;
    title: string;
    body: string;
    processor_name: string;
    contact: string;
    effective_from: string;
  } | null;
  /**
   * `null` berarti organisasi ini BELUM DIBUKA — keadaan, bukan galat. Layarnya
   * menyatakan apa yang kurang dan siapa yang harus bertindak.
   */
  accepted: {
    id: string;
    agreement_version: number;
    agreement_locale: string;
    accepted_by_name: string;
    accepted_by_title: string;
    accepted_at: string;
  } | null;
}

export interface TenantAuditRow {
  id: string;
  occurred_at: string;
  actor_kind: string;
  actor_name: string | null;
  event_code: string;
  subject_type: string;
  subject_id: string | null;
  module_code: string | null;
  ip: string | null;
  processing_basis: string | null;
}

export interface TenantAuditResponse {
  items: TenantAuditRow[];
  page_info: { has_more: boolean; next_cursor: string | null };
}

/**
 * IDENTITAS ORGANISASI — bentuk jawaban `GET/PATCH /v1/tenant/identity`.
 *
 * `logo_mark_url` adalah URL BERTANDA TANGAN berumur 90 detik dan TIDAK BOLEH
 * disimpan di mana pun oleh klien — tidak di localStorage, tidak di state yang
 * bertahan lintas layar. Yang tersimpan di server adalah `logo_mark_file_id`;
 * apa yang menyeberang ke sini sudah ditandatangani ulang (ADR-I2).
 *
 * `logo_state` KODE, bukan kalimat (ADR T1-06): layar merendernya dalam bahasa
 * pembacanya, dan ketiganya digambar Figma sebagai keadaan tersendiri.
 */
export interface TenantIdentity {
  tenant_id: string;
  slug: string | null;
  name: string | null;
  legal_name: string | null;
  plan_code: string | null;
  state: string | null;
  default_locale: string;
  timezone: string;
  product_name: string;
  accent_hex: string | null;
  logo_mark_url: string | null;
  logo_state: 'NONE' | 'VERIFYING' | 'READY';
  logo_byte_size: number | null;
  logo_width_px: number | null;
  logo_height_px: number | null;
}

/** Lingkup MGR. `legal_name`, `slug`, dan `plan_code` sengaja tidak ada (ADR-I1). */
export interface TenantIdentityPatch {
  name?: string;
  default_locale?: 'id-ID' | 'en-US';
  timezone?: string;
  accent_hex?: string;
  logo_mark_file_id?: string;
  hapus_logo?: boolean;
}

/** Lingkup PLT. Warna, zona waktu, bahasa, dan logo sengaja tidak ada (ADR-I1). */
export interface PlatformTenantPatch {
  name?: string;
  legal_name?: string;
  slug?: string;
}
